import Alert from "@mui/material/Alert";
import classNames from "classnames";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  renderPrincipal,
  useQuickSearchHotkey,
  useResourceView,
  ViewPicker,
  SaveViewControl,
  useLoadedView,
  useRecordTableState,
  useResourceTableContext,
  useRestoredTableState,
  useViewFromLink,
  type FiltersHandle,
  type Filter,
  type FilterDefinition,
  type QuickSearchProperty,
  type ResourceViewEmptyStateContent,
  type SelectionOption,
} from "@/shared/components/ResourceView";
import {
  CustomFieldRemovedColumnsAlert,
  useCustomFieldGridIntegration,
} from "@/shared/components/ResourceView/customFields";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { QueryContentMode, QueryTarget } from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import {
  type SessionIngestionSummary,
  tracksCompletion,
} from "@/shared/domain/sessions";
import {
  sessionQueryKeys,
  useSessionIngestionSummaries,
} from "@/shared/domain/sessions/hooks";
import { SessionRecord } from "@/shared/domain/sessions/SessionRecord";
import { useSessionTablePreferences } from "@/shared/domain/users/hooks";
import { ENTITY_ICONS } from "@/shared/foundation/icons";
import {
  ColumnVisibility,
  cellStyles,
  DataTable,
  FLEX_FILL_COL_DEF,
  buildColumnsForPreferences,
  colId,
  createColumnHelper,
  renderDashOr,
  type ColDef,
  type ColumnPickerOption,
  deriveLoadingState,
  type ICellRendererParams,
  type ValueFormatterParams,
  type ValueGetterParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation/Goto";
import { formatNanoDuration, nanoSecToDate } from "@/shared/time";
import type { SearchQueryBody } from "@/types";

import { SessionBadges } from "./SessionBadges";
import {
  sessionsQueryBody,
  useSessionFilterDefinitions,
} from "./sessionFilters";
import { sessionListStatus } from "./sessionListStatus";
import styles from "./SessionsList.module.css";

// Quick find routes an `se_`-shaped value to the id and everything else to the
// name, as the Datasets list does. The sessions query takes only exact matches
// on `id`.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  { field: "id", name: "ID", pattern: /^se_\S+$/, comparator: "EQUALS" },
  { field: "name", name: "Name", fallback: true },
];

const columnHelper = createColumnHelper<SessionRecord>();

/**
 * Normalize a session's nanosecond timestamp into a `Date` for `TimestampCell`,
 * or `null` (→ em dash) when absent. Sessions store these as `bigint`; the cell
 * only speaks instants.
 */
function nanosToDateOrNull(
  ns: bigint | number | undefined | null,
): Date | null {
  if (ns === undefined || ns === null) {
    return null;
  }
  return nanoSecToDate(BigInt(ns));
}

const durationColumn: ColDef<SessionRecord, bigint | null> = {
  colId: "duration",
  headerName: "Duration",
  minWidth: 120,
  sortable: true,
  cellClass: cellStyles.monoCell,
  valueGetter: (params: ValueGetterParams<SessionRecord>) => {
    const row = params.data;
    if (
      !row ||
      row.max_timestamp_ns === null ||
      row.min_timestamp_ns === null
    ) {
      return null;
    }
    return row.max_timestamp_ns - row.min_timestamp_ns;
  },
  valueFormatter: (
    params: ValueFormatterParams<SessionRecord, bigint | null>,
  ) => formatNanoDuration(params.value),
};

const STATIC_SESSION_COLUMNS: ColDef<SessionRecord>[] = [
  columnHelper.accessor("session_id", {
    headerName: "ID",
    minWidth: 170,
    cellRenderer: (params: ICellRendererParams<SessionRecord, string>) => {
      const id = params.value;
      if (!id) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createSessionRoute(id)}
          className={cellStyles.idCell}
        >
          {id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 3,
    minWidth: 200,
    sortable: true,
    // String projection for clipboard export / quick-filter / screen readers;
    // the visible cell renders the placeholder through `cellRenderer` below.
    valueFormatter: (params) => params.value ?? "Untitled session",
    cellRenderer: (params: ICellRendererParams<SessionRecord, string>) => {
      const name = params.value;
      const id = params.data?.session_id;
      if (!name) {
        return <span className={cellStyles.placeholder}>Untitled session</span>;
      }
      if (!id) {
        return <span className={cellStyles.primaryContent}>{name}</span>;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createSessionRoute(id)}
          className={cellStyles.primaryContent}
        >
          {name}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.timestamp("min_timestamp_ns", {
    headerName: "Start",
    minWidth: 120,
    sortable: true,
    variant: "precise",
    valueGetter: (params) => nanosToDateOrNull(params.data?.min_timestamp_ns),
  }),
  // Sort disabled: backend RoboQL catalog marks `max_timestamp_ns` non-sortable.
  // Re-enable once ENG-2522 enables it up server-side.
  columnHelper.timestamp("max_timestamp_ns", {
    headerName: "End",
    minWidth: 120,
    sortable: false,
    variant: "precise",
    valueGetter: (params) => nanosToDateOrNull(params.data?.max_timestamp_ns),
  }),
  durationColumn,
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    minWidth: 150,
    sortable: false,
    cellRenderer: renderPrincipal,
  }),
  // Sort omitted: backend RoboQL `SESSION_FIELDS` catalog registers
  // `tags` with `is_sortable=False`; including `sortable: true` would
  // surface a click target that always 400s.
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    minWidth: 180,
    cellClass: cellStyles.tagsCell,
    // String projection for AG Grid's clipboard export, quick filter,
    // and screen-reader announcements; the visible cell still renders
    // through `cellRenderer` below.
    valueFormatter: (params: ValueFormatterParams<SessionRecord, string[]>) =>
      (params.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (params: ICellRendererParams<SessionRecord, string[]>) => (
        <TagGroup tags={params.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
];

interface SummariesQuery {
  data: ReadonlyMap<string, SessionIngestionSummary> | undefined;
  isError: boolean;
}

/** The page's ingestion summaries, for the Status column's cells. */
const SummariesContext = React.createContext<SummariesQuery | null>(null);

/**
 * The status badge and, beside it, the ingestion badge. Reads the summaries
 * from context, so the column definitions don't change when they do: a new
 * summary re-renders the cell instead of remounting it and closing its popup.
 */
function StatusCell({ data: session }: ICellRendererParams<SessionRecord>) {
  const summaries = React.useContext(SummariesContext);
  if (session === undefined || summaries === null) {
    return null;
  }
  const summary = summaries.data?.get(session.session_id);
  const status = sessionListStatus(session, summary, {
    failed: summaries.isError,
  });
  return status === null ? null : (
    <span className={styles.statusCell}>
      <SessionBadges
        session={session}
        complete={status.complete}
        ingestion={status.ingestion}
        counts={summary}
        openOnHover={false}
      />
    </span>
  );
}

// Sort off on both: RoboQL does not yet say whether `status` and
// `ingested_at` are sortable, and a sort the backend rejects 400s.
const INGESTION_SESSION_COLUMNS: ColDef<SessionRecord>[] = [
  {
    colId: "status",
    headerName: "Status",
    // Room for "Complete" beside "Ingested · 2 not waited for".
    minWidth: 260,
    sortable: false,
    cellRenderer: StatusCell,
  },
  columnHelper.timestamp("ingested_at", {
    headerName: "Fully ingested at",
    minWidth: 150,
    sortable: false,
    variant: "precise",
  }),
];

const INGESTION_SESSION_COLUMN_IDS = new Set(
  INGESTION_SESSION_COLUMNS.map((c) => colId(c)),
);

function pickerOptions(columns: ColDef<SessionRecord>[]): ColumnPickerOption[] {
  return columns.map((c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }));
}

export interface SessionsListProps {
  /**
   * Applied to the outer container so the parent can size or position the list per page context.
   */
  className?: classNames.Argument;

  /**
   * Filters joined under an outer AND with the user's filter group, so
   * the user cannot widen results beyond them (e.g. the dataset tab pins
   * `dataset.dataset_id EQUALS <id>`). Hidden from the chip bar.
   *
   * The outer wrap is AND even when the user picks OR match mode; the
   * user's group nests inside so OR cannot dissolve the scope boundary.
   */
  scope?: Filter[];

  /**
   * Filterable fields the user can pick from. Omit to hide the filter
   * button and chip bar; the column-visibility picker and table still
   * render.
   */
  filterDefinitions?: FilterDefinition[];

  /**
   * The empty state shown when the unfiltered query returns no rows. The
   * icon and title default to the session icon and "No sessions yet".
   * Customize per-mount so the dataset and device tabs can scope the title
   * (e.g. "No sessions for this device") and the Sessions page can offer its
   * create button. When filters are active the list shows its own "no
   * matching sessions" state instead.
   */
  noData?: Partial<ResourceViewEmptyStateContent>;

  /**
   * Controls rendered at the right edge of the toolbar row, after the Filter
   * and Columns buttons. The Sessions page and dataset tab both pass their
   * "Create session" entry point here.
   */
  actions?: React.ReactNode;

  /**
   * Row-selection actions (e.g. "Remove from Collection"). Omit to hide the
   * selection checkbox column and the selection action menu entirely.
   */
  selectionOptions?: SelectionOption<SessionRecord>[];
}

/**
 * Paginated, filterable, sortable table of sessions. Submits a structured
 * query for the caller's org to obtain a `query_id`, then fetches a page
 * and total count off that id. Column order and page size persist per-user
 * via the shared session table preferences; sort is session-local.
 *
 * User filters always nest inside an outer AND with `scope`, so the
 * user's `matchMode` only governs how their own filters relate to each
 * other.
 *
 * Renders no chrome of its own. Consumers wrap it in `<PageLayout>` or
 * mount it inside an outlet so the same component fits either context.
 */
export function SessionsList({
  className,
  scope,
  filterDefinitions,
  noData,
  actions,
  selectionOptions,
}: SessionsListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useSessionTablePreferences();
  const ingestionEnabled = useCheckFeatureFlag(
    "release-session-ingestion",
    resolvedOrgId,
  );

  const customFields = useCustomFieldGridIntegration<SessionRecord>({
    entityType: TargetEntityType.Session,
    preferenceOrder: preferences.columns,
    savePreferences,
  });

  // Stored prefs are the source of truth; don't union with defaults on
  // mount — it would re-add columns the user explicitly removed.
  const staticColumns = React.useMemo(
    () =>
      ingestionEnabled
        ? [...STATIC_SESSION_COLUMNS, ...INGESTION_SESSION_COLUMNS]
        : STATIC_SESSION_COLUMNS,
    [ingestionEnabled],
  );

  // Without session ingestion, a saved `status` or `ingested_at` id would
  // otherwise render as an empty stub column.
  const preferenceOrder = React.useMemo(
    () =>
      ingestionEnabled
        ? customFields.effectivePreferenceOrder
        : customFields.effectivePreferenceOrder.filter(
            (id) => !INGESTION_SESSION_COLUMN_IDS.has(id),
          ),
    [ingestionEnabled, customFields.effectivePreferenceOrder],
  );

  const preferredColumns = React.useMemo<ColDef<SessionRecord>[]>(
    () =>
      buildColumnsForPreferences<SessionRecord>({
        staticColumns,
        dynamicColumns: customFields.customFieldColumns,
        preferenceOrder,
      }),
    [staticColumns, customFields.customFieldColumns, preferenceOrder],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...pickerOptions(staticColumns),
      ...customFields.columnPickerOptions,
    ],
    [staticColumns, customFields.columnPickerOptions],
  );

  const effectiveFilterDefinitions = useSessionFilterDefinitions(
    resolvedOrgId,
    filterDefinitions,
    customFields.filterDefinitions,
  );

  // A `?view=` link is resolved in the route loader, so the View it names
  // is available here — before the table's first query is composed. Seeding
  // it is what keeps a shared link from rendering an unfiltered page first.
  const linked = useViewFromLink<SessionRecord>({
    getRowId: (row) => row.session_id,
    initialSort: { columnId: "min_timestamp_ns", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });
  const restored = useRestoredTableState({
    target: QueryTarget.Sessions,
    link: linked,
  });
  const view = useResourceView<SessionRecord>(restored.options);
  const quickSearchState = useQuickSearchHotkey();

  // Which saved View is applied, and the routes in and out of one.
  const viewSession = useLoadedView(view, restored.view);
  useRecordTableState({
    target: QueryTarget.Sessions,
    view,
    loadedView: viewSession.loadedView,
  });
  useResourceTableContext({
    target: QueryTarget.Sessions,
    view,
    session: viewSession,
  });
  // Lets the View picker's empty state open the filter menu on a field.
  const filtersRef = React.useRef<FiltersHandle>(null);

  const query: SearchQueryBody = React.useMemo(
    () =>
      sessionsQueryBody({
        scope,
        filters: view.filters,
        definitions: effectiveFilterDefinitions,
        sort: view.table.sort,
      }),
    [scope, view.filters, view.table.sort, effectiveFilterDefinitions],
  );

  const queryKey = sessionQueryKeys.list(resolvedOrgId, query);

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Sessions,
    contentMode: QueryContentMode.RecordOnly,
    queryKey,
  });

  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<SessionRecord>(
    queryId,
    view.table.currentPage,
    view.table.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];
  // In-progress rows too: their ingestion badge shows files waiting or failed.
  const summaries = useSessionIngestionSummaries(
    rows.filter((row) => tracksCompletion(row)).map((row) => row.session_id),
    { enabled: ingestionEnabled, orgId: resolvedOrgId },
  );
  const summariesQuery = React.useMemo<SummariesQuery>(
    () => ({ data: summaries.data, isError: summaries.isError }),
    [summaries.data, summaries.isError],
  );

  const loading = deriveLoadingState({
    isLoading: submitQuery.isLoading || pageQuery.isLoading,
    isFetching: submitQuery.isFetching || pageQuery.isFetching,
  });
  // Prefer the submit error: the page fetch is disabled until submit
  // returns a `query_id`, so any error it surfaces is downstream of an
  // already-surfaced submit failure.
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);

  return (
    <div className={classNames(styles.container, className)}>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {userFacingErrorMessage(fetchError)}
        </Alert>
      )}
      <CustomFieldRemovedColumnsAlert {...customFields.removedColumnsAlert} />
      <QuickSearch
        open={quickSearchState.isOpen}
        onClose={quickSearchState.close}
        properties={QUICK_SEARCH_PROPERTIES}
        placeholder={"Search by ID or name…"}
        onSubmit={(filter) => {
          view.clearFilters();
          view.commit({ type: "structured", filter });
        }}
      />
      <ResourceView.Root>
        <ResourceView.Actions>
          <ViewPicker
            target={QueryTarget.Sessions}
            loadedView={viewSession.loadedView}
            onSelectView={viewSession.selectView}
            seededViewId={viewSession.seededViewId}
            arrivalClaimed={restored.arrivalClaimed}
            onClearView={viewSession.clearView}
            linkFor={viewSession.linkFor}
            filterDefinitions={effectiveFilterDefinitions}
            onStartFilter={(field) => filtersRef.current?.openWithField(field)}
          />
          <QuickSearchButton
            onClick={quickSearchState.open}
            label="Find sessions"
          />
          {effectiveFilterDefinitions?.length ? (
            <ResourceView.Filters
              ref={filtersRef}
              filterDefinitions={effectiveFilterDefinitions}
              onFilterSelected={view.commit}
              metrics
            />
          ) : null}
          <ColumnVisibility
            options={availableColumns}
            visibleColumnIds={view.table.visibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
          <ResourceView.Selection<SessionRecord>
            selection={view.table.selection}
            clearSelection={view.table.clearSelection}
            options={selectionOptions ?? []}
          />
          {actions ? (
            <div className={styles.trailingActions}>{actions}</div>
          ) : null}
        </ResourceView.Actions>
        {effectiveFilterDefinitions?.length ? (
          <ResourceView.ActiveFilters
            trailingActions={
              <SaveViewControl
                view={view}
                target={QueryTarget.Sessions}
                session={viewSession}
              />
            }
            filters={view.filters}
            filterDefinitions={effectiveFilterDefinitions}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
            metrics
          />
        ) : null}
        <SummariesContext.Provider value={summariesQuery}>
          <DataTable<SessionRecord>
            defaultColDef={FLEX_FILL_COL_DEF}
            table={view.table}
            hasNextPage={pageQuery.hasNextPage}
            loading={loading}
            columns={preferredColumns}
            data={rows}
            noRowsContent={
              <ResourceView.EmptyState
                hasActiveFilters={view.hasActiveFilters}
                noData={{
                  icon: ENTITY_ICONS.session,
                  title: "No sessions yet",
                  ...noData,
                }}
                noMatches={{
                  icon: ENTITY_ICONS.session,
                  title: "No matching sessions",
                  description: "Try adjusting or clearing your filters.",
                }}
              />
            }
            totalRowCount={countQuery.data}
            selectionEnabled={hasSelectionOptions}
          />
        </SummariesContext.Provider>
      </ResourceView.Root>
    </div>
  );
}
